'use client';
import React, { useRef } from 'react';

function StarShape({ filled, size }: { filled: boolean; size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="flex-shrink-0"
      fill={filled ? '#C6A667' : 'none'}
      stroke="#C6A667"
      strokeWidth={1.6}
      strokeLinejoin="round"
    >
      <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3 6.1 20.6l1.3-6.6L2.5 9.4l6.6-.8L12 2.5z" />
    </svg>
  );
}

interface StarDisplayProps {
  value: number;
  size?: number;
  label?: string;
}

export function StarDisplay({ value, size = 18, label }: StarDisplayProps) {
  const rounded = Math.round(value);
  return (
    <span className="inline-flex items-center gap-0.5" role="img" aria-label={label || `${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <StarShape key={n} filled={n <= rounded} size={size} />
      ))}
    </span>
  );
}

interface StarInputProps {
  value: number;
  onChange: (v: number) => void;
  describedBy?: string;
}

export function StarInput({ value, onChange, describedBy }: StarInputProps) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const move = (next: number) => {
    const clamped = Math.max(1, Math.min(5, next));
    onChange(clamped);
    refs.current[clamped - 1]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent, n: number) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      move((value || n) + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      move((value || n) - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'End') {
      e.preventDefault();
      move(5);
    }
  };

  return (
    <div role="radiogroup" aria-label="Star rating" aria-describedby={describedBy} className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          ref={(el) => {
            refs.current[n - 1] = el;
          }}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} star${n === 1 ? '' : 's'}`}
          tabIndex={value === n || (value === 0 && n === 1) ? 0 : -1}
          onClick={() => onChange(n)}
          onKeyDown={(e) => onKeyDown(e, n)}
          className="rounded-md p-1.5 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#581C32] focus-visible:ring-offset-2"
        >
          <StarShape filled={n <= value} size={34} />
        </button>
      ))}
    </div>
  );
}
